import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Inbox,
  Package,
  Printer,
  Truck,
  CalendarDays,
  ClipboardCheck,
  PackageCheck,
  BarChart3,
  Megaphone,
  SquareKanban,
  ScrollText,
  Ship,
  Sparkles,
  Handshake,
} from 'lucide-react';
import type { Permission } from '@shared/permissions';
import {
  REPORTS,
  reportIsAvailable,
  reportMatchesQuery,
  type ReportDefinition,
  type ReportFeature,
} from '@/reports/catalog';

/** Nested sidebar link under a parent NavItem (no icon). */
export interface NavChild {
  id: string;
  label: string;
  path: string;
  /**
   * Path prefixes that mark this child active (longest match wins).
   * Empty array = exact `path` only (used for Overview under Marketing).
   */
  activeMatchPrefixes: string[];
  /** Hidden from roles without it (the page shows "no access" for a direct link). */
  permission?: Permission;
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Path prefixes that mark this item active (longest match wins). */
  activeMatchPrefixes: string[];
  permission?: Permission;
  /** When the team is inactive, only items with this flag remain visible in the nav list. */
  inactiveTeamVisible: boolean;
  /** Optional expandable children (e.g. Marketing channels). */
  children?: NavChild[];
}

type NavMatchable = {
  path: string;
  activeMatchPrefixes: string[];
};

/** Report shortcuts for sidebar search — built once from the static catalog. */
export const REPORT_NAV_ITEMS: NavItem[] = REPORTS.map((report) => ({
  id: `report-${report.id}`,
  label: report.name,
  path: report.path,
  icon: BarChart3,
  activeMatchPrefixes: [report.path],
  inactiveTeamVisible: false,
}));

const REPORT_NAV_BY_ID = new Map<string, ReportDefinition>(
  REPORTS.map((report) => [`report-${report.id}`, report] as const)
);

const PRODUCT_CHILDREN: NavChild[] = [
  {
    id: 'products-overview',
    label: 'All products',
    path: '/products',
    activeMatchPrefixes: [
      '/products',
      '/products-bulk-edit',
      '/variant-details',
      '/product-details',
    ],
  },
  {
    id: 'products-batches',
    label: 'Batch Upload',
    path: '/product-batches',
    activeMatchPrefixes: ['/product-batches'],
  },
  {
    id: 'products-media',
    label: 'Media',
    path: '/media',
    activeMatchPrefixes: ['/media'],
  },
  {
    id: 'products-collections',
    label: 'Collections',
    path: '/collections',
    activeMatchPrefixes: ['/collections', '/collections-bulk-edit', '/collections-gaps'],
  },
  {
    id: 'products-landing-pages',
    label: 'Landing pages',
    path: '/landing-pages',
    activeMatchPrefixes: ['/landing-pages'],
  },
  {
    id: 'products-vendors',
    label: 'Brands',
    path: '/vendors',
    activeMatchPrefixes: ['/vendors'],
  },
  {
    id: 'products-product-types',
    label: 'Product Types',
    path: '/product-types',
    activeMatchPrefixes: ['/product-types'],
  },
  {
    id: 'products-swatches',
    label: 'Swatches',
    path: '/swatches',
    activeMatchPrefixes: ['/swatches'],
  },
];

const SUPPLIER_CHILDREN: NavChild[] = [
  {
    id: 'suppliers-overview',
    label: 'Suppliers',
    path: '/suppliers',
    activeMatchPrefixes: ['/suppliers'],
  },
  {
    id: 'suppliers-catalogues',
    label: 'Supplier Catalogues',
    path: '/suppliers/catalogues',
    activeMatchPrefixes: ['/suppliers/catalogues'],
  },
  {
    id: 'suppliers-changes',
    label: 'Supplier Changes',
    path: '/reports/supplier-changes',
    activeMatchPrefixes: ['/reports/supplier-changes'],
  },
];

const WHOLESALE_CHILDREN: NavChild[] = [
  {
    id: 'wholesale-packing',
    label: 'Packing',
    path: '/wholesale',
    activeMatchPrefixes: ['/wholesale'],
  },
  {
    id: 'wholesale-pricing',
    label: 'Pricing',
    path: '/wholesale/pricing',
    activeMatchPrefixes: ['/wholesale/pricing'],
  },
  {
    id: 'wholesale-price-lists',
    label: 'Price Lists',
    path: '/wholesale/price-lists',
    activeMatchPrefixes: ['/wholesale/price-lists'],
  },
  {
    id: 'wholesale-orders',
    label: 'Orders',
    path: '/wholesale/orders',
    activeMatchPrefixes: ['/wholesale/orders'],
  },
  {
    id: 'wholesale-customers',
    label: 'Customers',
    path: '/wholesale/customers',
    activeMatchPrefixes: ['/wholesale/customers'],
  },
  {
    id: 'wholesale-portal',
    label: 'Portal',
    path: '/wholesale/portal',
    activeMatchPrefixes: ['/wholesale/portal'],
  },
];

/** Trade customers on the B2B portal (T-218): retail less a discount. */
const TRADE_CHILDREN: NavChild[] = [
  { id: 'trade-orders', label: 'Orders', path: '/trade/orders', activeMatchPrefixes: ['/trade/orders'] },
  { id: 'trade-customers', label: 'Customers', path: '/trade/customers', activeMatchPrefixes: ['/trade/customers'] },
  { id: 'trade-discounts', label: 'Discounts', path: '/trade/discounts', activeMatchPrefixes: ['/trade/discounts'] },
  { id: 'trade-portal', label: 'Portal', path: '/trade/portal', activeMatchPrefixes: ['/trade/portal'] },
];

const IMPORTS_CHILDREN: NavChild[] = [
  { id: 'imports-overview', label: 'Overview', path: '/imports', activeMatchPrefixes: ['/imports/suppliers'] },
  { id: 'imports-price-lists', label: 'Price Lists', path: '/imports/price-lists', activeMatchPrefixes: ['/imports/price-lists'] },
  { id: 'imports-shipments', label: 'Shipments', path: '/imports/shipments', activeMatchPrefixes: ['/imports/shipments'] },
  { id: 'imports-price-changes', label: 'Price Changes', path: '/imports/price-changes', activeMatchPrefixes: ['/imports/price-changes'] },
  { id: 'imports-settings', label: 'Settings', path: '/imports/settings', activeMatchPrefixes: ['/imports/settings'] },
];

const PLANNING_CHILDREN: NavChild[] = [
  {
    id: 'planning-overview',
    label: 'Planning',
    path: '/planning',
    activeMatchPrefixes: [],
  },
  {
    id: 'planning-purchase-orders',
    label: 'Purchase Orders',
    path: '/planning/purchase-orders',
    activeMatchPrefixes: ['/planning/purchase-orders'],
  },
  {
    id: 'planning-overstock',
    label: 'Overstock',
    path: '/reports/overstock',
    activeMatchPrefixes: ['/reports/overstock'],
  },
  {
    id: 'planning-markdowns',
    label: 'Markdowns',
    path: '/reports/markdowns',
    activeMatchPrefixes: ['/reports/markdowns'],
  },
];

/** The Inbox and its settings (saved replies, signatures, who handles what, labels, sender rules). */
const INBOX_CHILDREN: NavChild[] = [
  {
    id: 'inbox-conversations',
    label: 'Conversations',
    path: '/inbox',
    activeMatchPrefixes: ['/inbox'],
  },
  {
    id: 'inbox-settings',
    label: 'Inbox settings',
    path: '/inbox-settings',
    activeMatchPrefixes: ['/inbox-settings'],
  },
];

const MARKETING_CHILDREN: NavChild[] = [
  {
    id: 'marketing-overview',
    label: 'Overview',
    path: '/marketing',
    activeMatchPrefixes: [],
  },
  {
    id: 'marketing-instagram',
    label: 'Instagram',
    path: '/marketing/instagram',
    activeMatchPrefixes: ['/marketing/instagram'],
  },
  {
    id: 'marketing-social-picks',
    label: 'Social picks',
    path: '/marketing/social-picks',
    activeMatchPrefixes: ['/marketing/social-picks'],
  },
  {
    id: 'marketing-post-ideas',
    label: 'Post ideas',
    path: '/marketing/post-ideas',
    activeMatchPrefixes: ['/marketing/post-ideas'],
  },
  {
    id: 'marketing-facebook',
    label: 'Facebook',
    path: '/marketing/facebook',
    activeMatchPrefixes: ['/marketing/facebook'],
  },
  {
    id: 'marketing-meta-ads',
    label: 'Meta Ads',
    path: '/marketing/meta-ads',
    activeMatchPrefixes: ['/marketing/meta-ads'],
  },
  {
    id: 'marketing-google-ads',
    label: 'Google Ads',
    path: '/marketing/google-ads',
    activeMatchPrefixes: ['/marketing/google-ads'],
  },
  {
    id: 'marketing-microsoft-ads',
    label: 'Microsoft Ads',
    path: '/marketing/microsoft-ads',
    activeMatchPrefixes: ['/marketing/microsoft-ads'],
  },
  {
    id: 'marketing-seo',
    label: 'SEO',
    path: '/marketing/seo',
    activeMatchPrefixes: ['/marketing/seo'],
  },
  {
    id: 'marketing-newsletters',
    label: 'Newsletters',
    path: '/marketing/newsletters',
    activeMatchPrefixes: ['/marketing/newsletters'],
  },
  {
    id: 'marketing-notifications',
    label: 'Customer notifications',
    path: '/marketing/notifications',
    activeMatchPrefixes: ['/marketing/notifications'],
  },
  {
    id: 'marketing-settings',
    label: 'Settings',
    path: '/marketing/settings',
    activeMatchPrefixes: ['/marketing/settings'],
    permission: 'SYNC_SETUP',
  },
];

/** AI: what the team's AI calls cost and do (Usage), and the prompts and models behind them (Prompts). */
const AI_CHILDREN: NavChild[] = [
  {
    id: 'ai-usage',
    label: 'Usage',
    path: '/ai-usage',
    activeMatchPrefixes: ['/ai-usage'],
  },
  {
    id: 'ai-prompts',
    label: 'Prompts',
    path: '/ai-prompts',
    activeMatchPrefixes: ['/ai-prompts'],
  },
];

/** Primary sidebar links. Management pages live in the footer team menu instead. */
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: Home,
    activeMatchPrefixes: ['/'],
    inactiveTeamVisible: true,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    path: '/tasks',
    icon: SquareKanban,
    activeMatchPrefixes: ['/tasks'],
    permission: 'TASKS',
    inactiveTeamVisible: false,
  },
  {
    id: 'inbox',
    label: 'Inbox',
    path: '/inbox',
    icon: Inbox,
    activeMatchPrefixes: ['/inbox', '/inbox-settings'],
    permission: 'INBOX',
    inactiveTeamVisible: false,
    children: INBOX_CHILDREN,
  },
  {
    id: 'policies',
    label: 'Policies',
    path: '/policies',
    icon: ScrollText,
    activeMatchPrefixes: ['/policies'],
    permission: 'POLICIES',
    inactiveTeamVisible: false,
  },
  {
    id: 'products',
    label: 'Products',
    path: '/products',
    icon: Package,
    activeMatchPrefixes: [
      '/products',
      '/products-bulk-edit',
      '/variant-details',
      '/product-details',
      '/media',
      '/collections',
      '/collections-bulk-edit',
      '/collections-gaps',
      '/vendors',
      '/product-types',
      '/swatches',
      '/product-batches',
    ],
    inactiveTeamVisible: false,
    children: PRODUCT_CHILDREN,
  },
  {
    id: 'labels',
    label: 'Labels',
    path: '/labels',
    icon: Printer,
    activeMatchPrefixes: ['/labels'],
    inactiveTeamVisible: false,
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    path: '/suppliers',
    icon: Truck,
    activeMatchPrefixes: ['/suppliers', '/suppliers/catalogues'],
    inactiveTeamVisible: false,
    children: SUPPLIER_CHILDREN,
  },
  {
    id: 'planning',
    label: 'Planning',
    path: '/planning',
    icon: CalendarDays,
    activeMatchPrefixes: [
      '/planning',
      '/planning/purchase-orders',
      '/reports/overstock',
      '/reports/markdowns',
    ],
    inactiveTeamVisible: false,
    children: PLANNING_CHILDREN,
  },
  {
    id: 'imports',
    label: 'Imports',
    path: '/imports',
    icon: Ship,
    activeMatchPrefixes: ['/imports'],
    permission: 'IMPORTS_VIEW',
    inactiveTeamVisible: false,
    children: IMPORTS_CHILDREN,
  },
  {
    id: 'receiving',
    label: 'Receiving',
    path: '/receiving',
    icon: ClipboardCheck,
    activeMatchPrefixes: ['/receiving'],
    permission: 'RECEIVING_VIEW',
    inactiveTeamVisible: false,
  },
  {
    id: 'wholesale',
    label: 'Wholesale',
    path: '/wholesale',
    icon: PackageCheck,
    activeMatchPrefixes: ['/wholesale'],
    permission: 'WHOLESALE_VIEW',
    inactiveTeamVisible: false,
    children: WHOLESALE_CHILDREN,
  },
  {
    id: 'trade',
    label: 'Trade',
    path: '/trade/orders',
    icon: Handshake,
    activeMatchPrefixes: ['/trade'],
    permission: 'WHOLESALE_VIEW',
    inactiveTeamVisible: false,
    children: TRADE_CHILDREN,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: BarChart3,
    activeMatchPrefixes: ['/reports'],
    inactiveTeamVisible: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    path: '/marketing',
    icon: Megaphone,
    activeMatchPrefixes: ['/marketing'],
    inactiveTeamVisible: false,
    children: MARKETING_CHILDREN,
  },
  {
    id: 'ai',
    label: 'AI',
    path: '/ai-usage',
    icon: Sparkles,
    activeMatchPrefixes: ['/ai-usage', '/ai-prompts'],
    permission: 'AI_PROMPTS_MANAGE',
    inactiveTeamVisible: false,
    children: AI_CHILDREN,
  },
];

/** Root `/` only matches home; other prefixes use startsWith. */
export function matchesNavPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') {
    return pathname === '/' || pathname === '/home';
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function pathMatchesNavItem(pathname: string, item: NavMatchable): boolean {
  if (pathname === item.path) return true;
  return item.activeMatchPrefixes.some((prefix) => matchesNavPrefix(pathname, prefix));
}

function scoreNavMatch(pathname: string, item: NavMatchable): number {
  if (!pathMatchesNavItem(pathname, item)) return -1;

  let score = pathname === item.path ? item.path.length : 0;
  for (const prefix of item.activeMatchPrefixes) {
    if (matchesNavPrefix(pathname, prefix)) {
      score = Math.max(score, prefix.length);
    }
  }
  return score;
}

/**
 * Among matching items (and their children), prefer the longest matching prefix
 * so nested routes activate the most specific item. Children win over parents
 * when scores are equal (e.g. Overview vs Marketing on `/marketing`).
 */
export function getActiveNavItemId(
  pathname: string,
  items: NavItem[]
): string | null {
  let bestId: string | null = null;
  let bestScore = -1;

  const consider = (id: string, score: number, preferOnTie: boolean) => {
    if (score < 0) return;
    if (score > bestScore || (score === bestScore && preferOnTie)) {
      bestScore = score;
      bestId = id;
    }
  };

  for (const item of items) {
    if (item.children?.length) {
      for (const child of item.children) {
        consider(child.id, scoreNavMatch(pathname, child), true);
      }
      // Parent only if no child claimed the route (or tied with lower priority).
      consider(item.id, scoreNavMatch(pathname, item), false);
    } else {
      consider(item.id, scoreNavMatch(pathname, item), false);
    }
  }

  return bestId;
}

function findNavLabel(activeId: string, items: NavItem[]): string | undefined {
  for (const item of items) {
    if (item.id === activeId) return item.label;
    const child = item.children?.find((c) => c.id === activeId);
    if (child) return child.label;
  }
  return undefined;
}

/** Titles for pages that are not in the sidebar (management pages from the top-bar menus). */
const EXTRA_PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/integrations', title: 'Integrations' },
  { prefix: '/team-settings', title: 'Team Settings' },
  { prefix: '/team', title: 'Team' },
  { prefix: '/link-shopify', title: 'Link Store' },
  { prefix: '/products-bulk-edit', title: 'Products Bulk Edit' },
  { prefix: '/product-batches', title: 'New product batches' },
  { prefix: '/collections-bulk-edit', title: 'Collections Bulk Edit' },
  { prefix: '/collections-gaps', title: 'Collection membership gaps' },
  { prefix: '/variant-details', title: 'Products' },
  { prefix: '/product-details', title: 'Products' },
  { prefix: '/product-content-queue', title: 'Queued content updates' },
];

/** Section title for the sticky top bar, resolved from the current route. */
export function getPageTitle(pathname: string): string {
  for (const entry of EXTRA_PAGE_TITLES) {
    if (pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)) {
      return entry.title;
    }
  }
  const activeId = getActiveNavItemId(pathname, NAV_ITEMS);
  if (!activeId) return '';
  return findNavLabel(activeId, NAV_ITEMS) ?? '';
}

/**
 * The browser tab's title, e.g. "Inbox - jflo", so tabs can be told apart. Reports go by their own
 * name; a section's first page by the section's ("Inbox", not "Conversations"); otherwise the top
 * bar's title. Plain "jflo" when the page has no name (sign-in). `recordTitle` (from PageHeader)
 * goes in front, so a record's tab names it.
 */
export function getDocumentTitle(pathname: string, recordTitle?: string | null): string {
  const report = REPORT_NAV_ITEMS.find((item) => matchesNavPrefix(pathname, item.path));
  const title = getPageTitle(pathname);
  const activeId = getActiveNavItemId(pathname, NAV_ITEMS);
  const section = NAV_ITEMS.find((item) =>
    item.children?.some((child) => child.id === activeId && child.path === item.path && child.label === title)
  );
  const page = report?.label ?? section?.label ?? title;
  // A record page names the record first (C23): "Order WO-0012 · Orders - jflo".
  const record = recordTitle?.trim();
  const name = record && record !== page ? (page ? `${record} · ${page}` : record) : page;
  return name ? `${name} - jflo` : 'jflo';
}

export function filterNavItems(
  items: NavItem[],
  options: {
    isTeamActive: boolean;
    hasPermission: (permission: Permission) => boolean;
    searchQuery?: string;
  }
): NavItem[] {
  const query = options.searchQuery?.trim().toLowerCase() ?? '';

  return items.flatMap((original) => {
    if (!options.isTeamActive && !original.inactiveTeamVisible) {
      return [];
    }
    if (original.permission && !options.hasPermission(original.permission)) {
      return [];
    }
    const item = original.children?.some((child) => child.permission)
      ? {
          ...original,
          children: original.children.filter(
            (child) => !child.permission || options.hasPermission(child.permission)
          ),
        }
      : original;

    if (!query) {
      return [item];
    }

    const parentMatches =
      item.label.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query);
    if (parentMatches) {
      return [item];
    }

    if (item.children?.length) {
      const matchingChildren = item.children.filter(
        (child) =>
          child.label.toLowerCase().includes(query) ||
          child.path.toLowerCase().includes(query)
      );
      if (matchingChildren.length > 0) {
        return [{ ...item, children: matchingChildren }];
      }
    }

    return [];
  });
}

/** Flatten parents + visible children for sidebar search keyboard navigation. */
export function flattenNavTargets(
  items: NavItem[]
): { id: string; path: string; label: string }[] {
  const targets: { id: string; path: string; label: string }[] = [];
  for (const item of items) {
    targets.push({ id: item.id, path: item.path, label: item.label });
    if (item.children?.length) {
      for (const child of item.children) {
        targets.push({ id: child.id, path: child.path, label: child.label });
      }
    }
  }
  return targets;
}

/**
 * Reports matching the sidebar search query (name, description, or category).
 * Empty when there is no search — reports stay on /reports, not the main nav.
 */
export function filterReportNavItems(
  searchQuery: string,
  isTeamActive: boolean,
  reportFeatures: ReadonlySet<ReportFeature>
): NavItem[] {
  const query = searchQuery.trim();
  if (!query || !isTeamActive) return [];

  return REPORT_NAV_ITEMS.filter((item) => {
    const report = REPORT_NAV_BY_ID.get(item.id);
    return report
      ? reportIsAvailable(report, reportFeatures) && reportMatchesQuery(report, query)
      : false;
  });
}

/** Report shortcuts for the team: the ones whose features (`requires`) it has on. */
export function availableReportNavItems(reportFeatures: ReadonlySet<ReportFeature>): NavItem[] {
  return REPORT_NAV_ITEMS.filter((item) => {
    const report = REPORT_NAV_BY_ID.get(item.id);
    return report ? reportIsAvailable(report, reportFeatures) : false;
  });
}
